/**
 * The one definition of a well-formed security path, shared by the builder, `family(...).for`,
 * `catalogue()` and the lookups. It applies the same shape rules as Security Controls'
 * `validatePath.ts` (the rule every write route uses), so nothing the registry hands out is a path
 * the write routes would refuse:
 *  - 1..700 characters (`SecurityEntry.SecurityPath` is `varchar(700)`);
 *  - printable ASCII only. The column is `varchar`, so anything else can be silently rewritten,
 *    and keeping to ASCII makes `toLowerCase()` agree with SQL Server's case-insensitive collation
 *    (no `İ`/`ß`/full-width/zero-width/NFC-vs-NFD look-alikes, red-team F-2/F-3);
 *  - no empty segment (a leading, trailing or doubled backslash), no `.`/`..` segment and no
 *    segment with leading or trailing whitespace.
 */

export const PATH_SEPARATOR = '\\';
export const MAX_PATH_LENGTH = 700;

const PRINTABLE_ASCII = /^[ -~]+$/;

/** Returns a description of the first rule `path` breaks, or `null` when it is well formed. */
export function pathProblem(path: string): string | null {
  if (typeof path !== 'string') return 'is not a string';
  if (path.length === 0) return 'is empty';
  if (path.length > MAX_PATH_LENGTH) return `is longer than ${MAX_PATH_LENGTH} characters`;
  if (!PRINTABLE_ASCII.test(path)) return 'contains a character that is not printable ASCII';
  if (path.startsWith(PATH_SEPARATOR)) return 'starts with a backslash';
  if (path.endsWith(PATH_SEPARATOR)) return 'ends with a backslash';
  if (path.includes(PATH_SEPARATOR + PATH_SEPARATOR)) return 'contains a doubled backslash';
  for (const segment of path.split(PATH_SEPARATOR)) {
    if (segment === '.' || segment === '..') return 'contains a "." or ".." segment';
    if (segment !== segment.trim()) return 'has a segment with leading or trailing whitespace';
  }
  return null;
}

/**
 * The case-insensitive comparison key of a path. Because every registry path is printable ASCII,
 * this is exactly the comparison SQL Server's case-insensitive collation makes. Compare
 * `SecurityPermission` values with this (or `samePermission`), never with `===` (F-3).
 */
export const pathKey = (path: string): string => path.toLowerCase();
